import moment, { Moment } from "moment";
import * as React from "react";
import { useContext, useEffect, useState, useCallback, RefObject } from "react";
import { AppContext } from "../../context/AppContext";
import Chart from "./index";
import DiaChartMapperInfluxPerformanceCustomer, { ApexOptions } from "./mapper/DiaChartMapperInfluxPerformanceCustomer";

/**
 * ChartPerformanceProps
 */
interface ChartPerformanceCustomerProps {
  from: string;
  to: string;
  onSetValues?: (from: string, to: string) => void,
  format?: string
}

/**
 * ChartPerformanceCustomer
 * @param from
 * @param to
 * @param onSetValues
 * @param tag
 * @param format
 * @constructor
 */
/** */
export default function ChartPerformanceCustomer(
  {
    from,
    to,
    onSetValues = (from: string, to: string) => {},
    format = "DD.MM.YYYY"
  }: ChartPerformanceCustomerProps
){
  // the default range is deliberately just today: at production data volume a
  // wider window delivers enough points to lock the renderer while apex builds
  // the chart -- users widen the range on purpose, the first paint stays light
  from = from || moment().format(format);
  to = to || moment().format(format);

  /**
   * elementRef
   */
  const elementRef: RefObject<HTMLElement> = React.createRef();

  /**
   * AppContext
   */
  const {
    I18N,
    services: {
      serviceInflux
    }
  } = useContext(AppContext);

  /**
   * series state
   */
  const [ series, setSeries ] = useState<ApexOptions['series']>();

  /**
   * request state: loading shows a visible indicator, error a panel with
   * retry -- without it the chart area just stayed blank
   */
  const [ status, setStatus ] = useState<'loading' | 'ready' | 'error'>('loading');

  /**
   * apex chart options
   */
  const options: ApexOptions = {
    xaxis: {
      tickAmount: 5,
      title: {
        text: I18N.t("Datum/Uhrzeit")
      },
      type: "datetime"
    },
    yaxis: {
      tickAmount: 5,
      title: {
        text: I18N.t("Wert")
      },
      type: "numeric",
      decimalsInFloat: 2
    },
    chart: {
      redrawOnParentResize: true,
      redrawOnWindowResize: true,
      fontFamily: 'Inter, Arial, sans-serif',
      zoom: {
        enabled: false
      },
      toolbar: {
        tools: {
          download: '<button data-secondary-light data-icon-only data-small title="'+I18N.t("Download als SVG, PNG, CSV")+'"><i data-icon-before="download"></i></button>',
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false
        }
      }
    },
    noData: {
      // apex paints this whenever the series is empty -- also during the
      // first load, where the loading line below already says what happens,
      // so the text only appears once the fetch settled without data
      text: status === 'loading' ? "" : I18N.t('Keine Daten verfügbar')
    }
  };

  /**
   * loadPerformanceData
   */
  const loadPerformanceData= useCallback(async () => {
    /**
     * check for empty values
     */
    if (from?.length === 0 || to?.length === 0) return;


    /**
     * parse date by format to send Date Object to API Call
     */
    let parseFrom: Moment = moment(from, format),
      parseTo: Moment = moment(to, format),
      parseFromDate: Date,
      parseToDate: Date;

    parseFrom = !parseFrom.isValid() ? moment(from, format) : parseFrom;
    parseTo = !parseTo.isValid() ? moment(to, format) : parseTo;

    parseFromDate = parseFrom.toDate();
    parseToDate = parseTo.toDate();

    /**
     * if from and to date exact the same we create a diff of ca. 24 hours
     */
    if (parseFromDate.toISOString() === parseToDate.toISOString()) {
      parseFromDate.setHours(0);
      parseFromDate.setMinutes(0);
      parseFromDate.setSeconds(0);

      parseToDate.setHours(23);
      parseToDate.setMinutes(59);
      parseToDate.setSeconds(59);
    }

    /**
     * invoke callback to post values to parent listener if it exists
     */
    if (from && to) {
      onSetValues(from, to);
    }

    /**
     * get performance data from service
     */
    // a failed fetch used to die as an unhandled rejection and the panel
    // stayed blank forever; now it surfaces as a visible error state
    try {
      setStatus('loading');

      let performance = await serviceInflux.performanceCustomerData(parseFromDate, parseToDate);

      /**
       * map influx data to apex chart format
       */
      let diaChartMapperPerformance: DiaChartMapperInfluxPerformanceCustomer = new DiaChartMapperInfluxPerformanceCustomer(performance);

      /**
       * update series state with mapped data
       */
      setSeries(diaChartMapperPerformance.getSeries());
      setStatus('ready');
    } catch (error) {
      console.error('Error fetching chart data:', error);
      setStatus('error');
    }
  // eslint-disable-next-line
  }, [from, to]);

  /**
   * get performance data from api and map influx data to apex format by mapper
   */
  useEffect(() => {
    loadPerformanceData();
  }, [ loadPerformanceData ]);

  /**
   * render performance chart with series data
   */
  if (status === 'error') {
    return (
      <div data-chart-error role="alert">
        <p>{ I18N.t("Die Daten konnten nicht geladen werden.") }</p>
        <button
          data-secondary
          data-small
          onClick={ () => loadPerformanceData() }
        >
          { I18N.t("Erneut versuchen") }
        </button>
      </div>
    );
  }

  return (
    <>
      { /* the live region exists from the first render on and only its text
           toggles: a region inserted together with its content is usually
           not announced at all */ }
      <p data-chart-loading aria-live="polite">
        { status === 'loading' ? I18N.t("Daten werden geladen …") : "" }
      </p>
      <Chart
        // @ts-ignore
        ref={ elementRef }
        type="scatter"
        series={ series }
        options={ options }
      ></Chart>
    </>
  );
}
